/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { createDkvRegistration } from '../fn/dkv-broker/create-dkv-registration';
import { CreateDkvRegistration$Params } from '../fn/dkv-broker/create-dkv-registration';
import { DkvBrokerRegistrationDto } from '../models/dkv-broker-registration-dto';
import { getDkvRegistration } from '../fn/dkv-broker/get-dkv-registration';
import { GetDkvRegistration$Params } from '../fn/dkv-broker/get-dkv-registration';
import { terminate } from '../fn/dkv-broker/terminate';
import { Terminate$Params } from '../fn/dkv-broker/terminate';
import { updateLimits } from '../fn/dkv-broker/update-limits';
import { UpdateLimits$Params } from '../fn/dkv-broker/update-limits';
import { updateLock } from '../fn/dkv-broker/update-lock';
import { UpdateLock$Params } from '../fn/dkv-broker/update-lock';

@Injectable({ providedIn: 'root' })
export class DkvBrokerService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getDkvRegistration()` */
  static readonly GetDkvRegistrationPath = '/svgs/{svgId}/customers/{customerId}/registrations';

  /**
   * Retrieve DKV Registration and its Customer Submission via PDI.
   *
   * Retrieves a DKVRegistration and check for status of open customer submissions via pdi.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDkvRegistration()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDkvRegistration$Response(params: GetDkvRegistration$Params, context?: HttpContext): Observable<StrictHttpResponse<DkvBrokerRegistrationDto>> {
    return getDkvRegistration(this.http, this.rootUrl, params, context);
  }

  /**
   * Retrieve DKV Registration and its Customer Submission via PDI.
   *
   * Retrieves a DKVRegistration and check for status of open customer submissions via pdi.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDkvRegistration$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDkvRegistration(params: GetDkvRegistration$Params, context?: HttpContext): Observable<DkvBrokerRegistrationDto> {
    return this.getDkvRegistration$Response(params, context).pipe(
      map((r: StrictHttpResponse<DkvBrokerRegistrationDto>): DkvBrokerRegistrationDto => r.body)
    );
  }

  /** Path part for operation `createDkvRegistration()` */
  static readonly CreateDkvRegistrationPath = '/svgs/{svgId}/customers/{customerId}/registrations';

  /**
   * Create a DKVRegistration and sync with DKV for cockpit access.
   *
   * Create a DKVRegistration, which creates a Customer via PDI for DKV, which enables the customer in cockpit (for staff users access). This will trigger an async process, and will take up to an hour, until that customer is fully created.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDkvRegistration()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvRegistration$Response(params: CreateDkvRegistration$Params, context?: HttpContext): Observable<StrictHttpResponse<DkvBrokerRegistrationDto>> {
    return createDkvRegistration(this.http, this.rootUrl, params, context);
  }

  /**
   * Create a DKVRegistration and sync with DKV for cockpit access.
   *
   * Create a DKVRegistration, which creates a Customer via PDI for DKV, which enables the customer in cockpit (for staff users access). This will trigger an async process, and will take up to an hour, until that customer is fully created.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDkvRegistration$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvRegistration(params: CreateDkvRegistration$Params, context?: HttpContext): Observable<DkvBrokerRegistrationDto> {
    return this.createDkvRegistration$Response(params, context).pipe(
      map((r: StrictHttpResponse<DkvBrokerRegistrationDto>): DkvBrokerRegistrationDto => r.body)
    );
  }

  /** Path part for operation `terminate()` */
  static readonly TerminatePath = '/svgs/{svgId}/customers/{customerId}/registrations';

  /**
   * Terminate a DKVRegistration, which is only possible for a locked DKVRegistration.
   *
   * Terminate a DKVRegistration, terminate the customer via pdi and terminate the contract via svgzapi-core. DKV does not lock a customer automatically when termination is called. Therefore this is only allowed for locked DKVRegistrations.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `terminate()` instead.
   *
   * This method doesn't expect any request body.
   */
  terminate$Response(params: Terminate$Params, context?: HttpContext): Observable<StrictHttpResponse<DkvBrokerRegistrationDto>> {
    return terminate(this.http, this.rootUrl, params, context);
  }

  /**
   * Terminate a DKVRegistration, which is only possible for a locked DKVRegistration.
   *
   * Terminate a DKVRegistration, terminate the customer via pdi and terminate the contract via svgzapi-core. DKV does not lock a customer automatically when termination is called. Therefore this is only allowed for locked DKVRegistrations.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `terminate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  terminate(params: Terminate$Params, context?: HttpContext): Observable<DkvBrokerRegistrationDto> {
    return this.terminate$Response(params, context).pipe(
      map((r: StrictHttpResponse<DkvBrokerRegistrationDto>): DkvBrokerRegistrationDto => r.body)
    );
  }

  /** Path part for operation `updateLimits()` */
  static readonly UpdateLimitsPath = '/svgs/{svgId}/customers/{customerId}/registrations/limits';

  /**
   * Update DKVRegistration limits.
   *
   * Update limits of a DKVRegistration via pdi. Those are limits on the customer, over all of the customers DKV products (eets boxes and fuel cards)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateLimits()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateLimits$Response(params: UpdateLimits$Params, context?: HttpContext): Observable<StrictHttpResponse<DkvBrokerRegistrationDto>> {
    return updateLimits(this.http, this.rootUrl, params, context);
  }

  /**
   * Update DKVRegistration limits.
   *
   * Update limits of a DKVRegistration via pdi. Those are limits on the customer, over all of the customers DKV products (eets boxes and fuel cards)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateLimits$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateLimits(params: UpdateLimits$Params, context?: HttpContext): Observable<DkvBrokerRegistrationDto> {
    return this.updateLimits$Response(params, context).pipe(
      map((r: StrictHttpResponse<DkvBrokerRegistrationDto>): DkvBrokerRegistrationDto => r.body)
    );
  }

  /** Path part for operation `updateLock()` */
  static readonly UpdateLockPath = '/svgs/{svgId}/customers/{customerId}/registrations/lock';

  /**
   * Update lock state of a DKVRegistration.
   *
   * Update lock state of a DKVRegistration, and send sync it via pdi. This is a rest endpoint on pdi and will be done immediately
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateLock()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateLock$Response(params: UpdateLock$Params, context?: HttpContext): Observable<StrictHttpResponse<DkvBrokerRegistrationDto>> {
    return updateLock(this.http, this.rootUrl, params, context);
  }

  /**
   * Update lock state of a DKVRegistration.
   *
   * Update lock state of a DKVRegistration, and send sync it via pdi. This is a rest endpoint on pdi and will be done immediately
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateLock$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateLock(params: UpdateLock$Params, context?: HttpContext): Observable<DkvBrokerRegistrationDto> {
    return this.updateLock$Response(params, context).pipe(
      map((r: StrictHttpResponse<DkvBrokerRegistrationDto>): DkvBrokerRegistrationDto => r.body)
    );
  }

}
